/**
 * The warning shown when a processed document has something worth a second
 * look before it is stored. The checks themselves live in `save-review`; this
 * only says what they found, and offers the two ways out.
 *
 * It never blocks: the operator can see the page and the model cannot, so the
 * last word is theirs. The point is only that nobody stores a run without
 * having been told what looks wrong with it.
 */
import { Dialog } from 'sharedUi/Dialog';
import { Button } from 'sharedUi/Button';
import { Icon } from '../../components/common/icon';
import type { SaveWarning } from './save-review';

const GROUPS: { kind: SaveWarning['kind']; heading: string }[] = [
  { kind: 'category', heading: 'Not identified' },
  { kind: 'missing', heading: 'Required fields with no value' },
  { kind: 'type', heading: 'Values that do not match their type' },
  { kind: 'confidence', heading: 'Read with low confidence' },
];

interface SaveReviewDialogProps {
  visible: boolean;
  warnings: SaveWarning[];
  saving: boolean;
  /** Back to the screen to fix something. */
  onGoBack: () => void;
  /** Save it as it stands. */
  onSaveAnyway: () => void;
}

const SaveReviewDialog = ({ visible, warnings, saving, onGoBack, onSaveAnyway }: SaveReviewDialogProps) => {
  const groups = GROUPS
    .map((g) => ({ ...g, items: warnings.filter((w) => w.kind === g.kind) }))
    .filter((g) => g.items.length > 0);

  return (
    <Dialog
      visible={visible}
      onHide={onGoBack}
      header="Check before saving"
      style={{ width: '560px', maxWidth: '92vw' }}
      footer={(
        <div className="flex justify-end gap-3">
          <Button label="Go back" severity="secondary" outlined size="small"
            onClick={onGoBack} disabled={saving}
            testId="SaveReviewDialog.Button.GoBack" />
          <Button label={saving ? 'Saving...' : 'Save anyway'} severity="warning" size="small"
            icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-save'}
            onClick={onSaveAnyway} disabled={saving}
            testId="SaveReviewDialog.Button.SaveAnyway" />
        </div>
      )}
    >
      <div data-testid="SaveReviewDialog">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 shrink-0 text-amber-500">
            <Icon name="alert-triangle" size={20} />
          </span>
          <p className="m-0 text-body-sm text-ink">
            {warnings.length} thing{warnings.length === 1 ? '' : 's'} to look at in this document.
            You can save it as it is — a document that really does not state a value is worth
            keeping that way — or go back and correct it first.
          </p>
        </div>

        <div className="mt-4 max-h-80 space-y-4 overflow-y-auto pr-1">
          {groups.map((group) => (
            <div key={group.kind}>
              <p className="text-label font-medium text-ink-muted">{group.heading}</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {group.items.map((warning) => (
                  <li key={warning.message} className="text-body-sm text-ink">{warning.message}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
};

export default SaveReviewDialog;
